import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { smsWindowApi } from '../smsWindowApi';
import { useAuth } from '../AuthContext';
import PickSelect from '../shared/PickSelect';

const hourLabel = (h) => (h === 24 ? '12:00 AM (midnight)' : `${h % 12 || 12}:00 ${h < 12 ? 'AM' : 'PM'}`);
const hours = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i)
  .map((h) => ({ value: String(h), label: hourLabel(h) }));
const ADMIN_ROLES = ['owner', 'admin'];

// The hours texts may go out. One window for the whole workspace, set by an owner or admin.
export default function TextingHoursSection() {
  const { user } = useAuth();
  const canEdit = !!user?.is_super_admin || ADMIN_ROLES.includes(user?.membership_role);
  const [win, setWin] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);

  const apply = (w) => { setWin(w); setStart(String(w.start_hour)); setEnd(String(w.end_hour)); };

  useEffect(() => {
    smsWindowApi.get().then(apply)
      .catch((e) => toast({ title: 'Could not load texting hours', description: errorMessage(e) }));
  }, []);

  const run = async (call, title) => {
    setSaving(true);
    try {
      apply(await call());
      toast({ title });
    } catch (e) {
      toast({ title: 'Could not save texting hours', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  if (!win) {
    return (
      <div className="flex items-center text-slate-400 text-sm" data-testid="texting-hours-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading...
      </div>
    );
  }

  const startOptions = hours(win.default_start_hour, win.default_end_hour - 1);
  const endOptions = hours(win.default_start_hour + 1, win.default_end_hour);
  const invalid = Number(start) >= Number(end);
  const dirty = Number(start) !== win.start_hour || Number(end) !== win.end_hour;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg space-y-3" data-testid="texting-hours">
      <div>
        <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Texting hours</h2>
        <p className="text-sm text-slate-500 mt-1">
          Texts go out only between these hours, in your company time zone ({win.timezone}) and in the recipient's time zone when we know their state.
          {' '}{win.custom ? 'This is your own setting.' : 'This is the company default.'}
          {' '}You can shorten the hours but not extend them past {hourLabel(win.default_start_hour)} to {hourLabel(win.default_end_hour)}.
        </p>
      </div>
      {canEdit ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <PickSelect label="From" value={start} options={startOptions} onChange={setStart} testId="texting-hours-start" />
            <PickSelect label="Until" value={end} options={endOptions} onChange={setEnd} testId="texting-hours-end" />
          </div>
          {invalid && <p className="text-xs text-red-600" data-testid="texting-hours-invalid">The first hour must come before the last hour.</p>}
          <div className="flex items-center gap-2">
            <button type="button" disabled={saving || invalid || !dirty}
              onClick={() => run(() => smsWindowApi.set(Number(start), Number(end)), 'Texting hours saved')}
              className="text-xs px-3 py-1.5 rounded bg-[#070B5F] text-white disabled:opacity-50" data-testid="texting-hours-save">
              {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
            </button>
            {win.custom && (
              <button type="button" disabled={saving}
                onClick={() => run(() => smsWindowApi.reset(), 'Back to the company default')}
                className="text-xs px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50" data-testid="texting-hours-reset">
                Use company default
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm" data-testid="texting-hours-readonly">
          {hourLabel(win.start_hour)} to {hourLabel(win.end_hour)}. Only an owner or admin can change this.
        </p>
      )}
    </div>
  );
}
